import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageSquareText, ChevronDown, MapPin, User, LogOut } from "lucide-react";
import { siteConfig } from "../config";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";
import GlobalSearchBar from "./GlobalSearchBar";
import { NEW_CATEGORY_LINKS, USED_CATEGORY_LINKS } from "../lib/inventoryCategories";

// "New Chevrolet SUVs" intentionally goes straight to /new-vehicles/equinox
// (Scott's flagship featured SUV) per user preference. Other categories
// still use the body_style filter pages.
const NEW_DROPDOWN_LINKS = NEW_CATEGORY_LINKS.map(([txt, href, testid]) =>
  txt === "New Chevrolet SUVs" ? [txt, "/new-vehicles/equinox", testid] : [txt, href, testid]
);

const DROPDOWNS = {
  "New Vehicles": {
    href: "/new-vehicles",
    items: [
      ["Search New Inventory", "/new-vehicles"],
      ...NEW_DROPDOWN_LINKS.map(([txt, href]) => [txt, href]),
      ["Commercial & Work Trucks", "/new-vehicles/commercial"],
      ["New Silverado", "/new-vehicles/silverado"],
      ["New Colorado", "/new-vehicles/colorado"],
      ["New Equinox", "/new-vehicles/equinox"],
      ["New Traverse", "/new-vehicles/traverse"],
      ["New Blazer", "/new-vehicles/blazer"],
      ["New Trax", "/new-vehicles/trax"],
      ["New Tahoe", "/new-vehicles/tahoe"],
      ["New Suburban", "/new-vehicles/suburban"],
    ],
  },
  "Used Vehicles": {
    href: "/used-vehicles",
    items: [
      ["Search Used Inventory", "/used-vehicles"],
      ...USED_CATEGORY_LINKS.map(([txt, href]) => [txt, href]),
      ["Used Vehicles Under $20,000", "/used-vehicles/under-20000"],
      ["Used Vehicles Under $25,000", "/used-vehicles/under-25000"],
      ["Low Mileage Used Vehicles", "/used-vehicles/low-mileage"],
      ["Used AWD & 4x4", "/used-vehicles/awd-4x4"],
    ],
  },
  "Shop by Need": {
    href: "/shop-by-need",
    items: [
      ["I Need a Truck", "/shop-by-need/trucks"],
      ["I Need an SUV", "/shop-by-need/suvs"],
      ["I Want an EV", "/shop-by-need/evs"],
      ["I Need AWD / 4x4", "/shop-by-need/awd-4x4"],
      ["I Need a Family Vehicle", "/shop-by-need/family-vehicles"],
      ["I'm Shopping by Payment", "/shop-by-need/payment"],
      ["I Have a Trade", "/value-my-trade"],
      ["First-Time Buyer Picks", "/shop-by-need/first-time-buyer"],
      ["Work Vehicles", "/shop-by-need/work-vehicles"],
      ["Commuter Vehicles", "/shop-by-need/commuter-vehicles"],
    ],
  },
  "Finance & Trade": {
    href: "/finance-trade",
    items: [
      ["Payment Help", "/payment-help"],
      ["Value My Trade", "/value-my-trade"],
      ["Build My Own Deal", "/build-my-own-deal"],
      ["Lease Help", "/lease-help"],
      ["Get Pre-Qualified", siteConfig.links.financeApply, true],
      ["Apply for Financing", siteConfig.links.financeApply, true],
      ["Trade-In Checklist", "/trade-in-checklist"],
      ["Payment Calculator", "/payment-calculator"],
      ["Shop Chevrolet Accessories", siteConfig.links.accessories, true],
    ],
  },
  Resources: {
    href: "/resources",
    items: [
      ["EV Help", "/ev-help"],
      ["Vehicle Alerts", "/vehicle-alerts"],
      ["Reviews", "/reviews"],
      ["FAQs", "/faq"],
      ["Service Areas", "/service-areas"],
      ["Car Buying Tips", "/car-buying-tips"],
      ["New vs Used Guide", "/new-vs-used"],
      ["Contact Scott", "/contact"],
    ],
  },
};

const MOBILE_LINKS = [
  ["Home", "/"],
  ["New Vehicles", "/new-vehicles"],
  ["Used Vehicles", "/used-vehicles"],
  ["Find Me a Vehicle", "/find-me-a-vehicle"],
  ["Build My Own Deal", "/build-my-own-deal"],
  ["Value My Trade", "/value-my-trade"],
  ["Payment Help", "/payment-help"],
  ["Apply for Financing", siteConfig.links.financeApply, true],
  ["Shop Accessories", siteConfig.links.accessories, true],
  ["EV Help", "/ev-help"],
  ["Vehicle Alerts", "/vehicle-alerts"],
  ["Reviews", "/reviews"],
  ["Service Areas", "/service-areas"],
  ["About Scott", "/about-scott"],
  ["Contact", "/contact"],
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const loc = useLocation();
  const navRef = useRef(null);
  useEffect(() => {
    setOpen(false);
    setOpenDropdown(null);
  }, [loc.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);
  // Close dropdown on outside click
  useEffect(() => {
    if (!openDropdown) return;
    function onClick(e) {
      if (!navRef.current?.contains(e.target)) setOpenDropdown(null);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpenDropdown(null);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openDropdown]);

  return (
    <header className="ssc-header" data-testid="site-header">
      <a className="ssc-skip" href="#main-content">Skip to content</a>
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 flex items-center gap-3 py-3 lg:py-4">
        {/* Logo */}
        <Link to="/" className="flex items-center mr-1 shrink-0" data-testid="header-logo-link" aria-label="Scott Sells Chevys home">
          <img
            src={siteConfig.logo.url}
            alt={siteConfig.logo.alt}
            className="h-14 sm:h-16 md:h-20 xl:h-24 w-auto"
            width="280" height="80"
          />
        </Link>

        {/* Desktop nav */}
        <nav ref={navRef} className="ml-2 hidden xl:flex items-center gap-1" aria-label="Primary">
          <Link to="/" className={`ssc-nav-link ${loc.pathname === "/" ? "is-active" : ""}`} data-testid="nav-home">Home</Link>
          {Object.entries(DROPDOWNS).map(([label, def]) => {
            const isOpen = openDropdown === label;
            return (
              <div key={label} className={`relative ssc-has-dropdown ${isOpen ? "is-open" : ""}`}>
                <button
                  type="button"
                  className="ssc-nav-link"
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  onClick={() => setOpenDropdown(isOpen ? null : label)}
                  data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {label} <ChevronDown size={14} />
                </button>
                <div className="ssc-dropdown" role="menu">
                  {def.items.map(([txt, href, ext], i) => {
                    const tid = `dropdown-${txt.toLowerCase().replace(/[^a-z]+/g, "-")}`;
                    const handleItemClick = (e) => {
                      setOpenDropdown(null);
                      // Drop focus so :focus-within doesn't keep the menu open.
                      e.currentTarget.blur();
                    };
                    return ext ? (
                      <a
                        key={i}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleItemClick}
                        data-testid={tid}
                      >
                        {txt}
                      </a>
                    ) : (
                      <Link
                        key={i}
                        to={href}
                        onClick={handleItemClick}
                        data-testid={tid}
                      >
                        {txt}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <Link to="/about-scott" className="ssc-nav-link" data-testid="nav-about-scott">About Scott</Link>
          <Link to="/contact" className="ssc-nav-link" data-testid="nav-contact">Contact</Link>
        </nav>

        {/* Right side: stacked layout — compact Sign In on TOP row, search
            bar BELOW (per v6.4 redesign). The Contact button on the far right
            is removed because Contact already exists in the nav and the
            page has multiple chat/text/call CTAs. */}
        <div className="ml-auto flex items-stretch gap-2 shrink-0" data-testid="header-right">
          <div className="hidden xl:flex flex-col items-end gap-1.5">
            <AccountButton />
            <div className="min-w-[260px] max-w-[340px]" data-testid="header-global-search-wrap">
              <GlobalSearchBar variant="header" placeholder="Search by model, stock # or VIN" />
            </div>
          </div>
          <button
            type="button"
            aria-label="Open menu"
            className="xl:hidden ssc-mobile-toggle"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
          >
            <Menu size={22} />
          </button>
        </div>
      </div>

      {/* Mobile slide-out */}
      {open && (
        <div className="fixed inset-0 z-[80] xl:hidden h-screen" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute right-0 top-0 h-screen w-[88%] max-w-[420px] bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-extrabold text-[var(--ssc-navy)]">Menu</div>
              <button className="ssc-btn ssc-btn-outline" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
                <X size={20} />
              </button>
            </div>
            <div className="p-3 grid grid-cols-2 gap-2 border-b">
              <a href={siteConfig.scott.phoneTel} className="ssc-btn ssc-btn-navy" data-testid="mobile-menu-call">
                <Phone size={18} /> Call Scott
              </a>
              <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary" data-testid="mobile-menu-text">
                <MessageSquareText size={18} /> Text Scott
              </a>
            </div>
            <div className="px-3 py-3 border-b" data-testid="mobile-menu-search-wrap">
              <GlobalSearchBar
                variant="mobile"
                placeholder="Search by model, stock #, VIN…"
                onNavigate={() => setOpen(false)}
              />
            </div>
            <nav className="flex-1 overflow-y-auto p-2" aria-label="Mobile">
              {MOBILE_LINKS.map(([txt, href, ext], i) =>
                ext ? (
                  <a key={i} href={href} target="_blank" rel="noopener noreferrer"
                     className="block px-4 py-3 rounded-xl font-semibold text-[var(--ssc-navy)] hover:bg-[var(--ssc-silver-2)]"
                     data-testid={`mobile-link-${txt.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {txt} ↗
                  </a>
                ) : (
                  <Link key={i} to={href}
                        className="block px-4 py-3 rounded-xl font-semibold text-[var(--ssc-navy)] hover:bg-[var(--ssc-silver-2)]"
                        data-testid={`mobile-link-${txt.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {txt}
                  </Link>
                )
              )}
            </nav>
          </aside>
        </div>
      )}
    </header>
  );
}

function AccountButton() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const close = () => setOpen(false);
    if (open) {
      document.addEventListener("click", close);
      return () => document.removeEventListener("click", close);
    }
  }, [open]);

  // Not logged in OR still hydrating → show "Sign In"
  if (!user) {
    return (
      <Link
        to="/login"
        className="ssc-btn-pill ssc-btn-pill-outline-dark hidden sm:inline-flex"
        data-testid="header-signin"
      >
        <User size={16} /> Sign In
      </Link>
    );
  }

  const isDealer = isDealerUser(user);

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="ssc-btn-pill ssc-btn-pill-outline-dark inline-flex"
        data-testid="header-account"
      >
        <User size={16} />
        <span className="hidden sm:inline">{(user.name || user.email || "Account").split(" ")[0]}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-[110%] z-50 bg-white rounded-xl shadow-2xl border border-[var(--ssc-line)] py-2 w-52" data-testid="header-account-menu">
          {isDealer ? (
            <Link to="/dealer" className="block px-4 py-2 text-sm font-semibold text-[var(--ssc-navy)] hover:bg-[var(--ssc-paper)]" data-testid="header-account-dealer">Dealer Portal</Link>
          ) : (
            <Link to="/my-account" className="block px-4 py-2 text-sm font-semibold text-[var(--ssc-navy)] hover:bg-[var(--ssc-paper)]" data-testid="header-account-mine">My Account</Link>
          )}
          <button
            onClick={async () => { await logout(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm font-semibold text-red-600 hover:bg-[var(--ssc-paper)] inline-flex items-center gap-2"
            data-testid="header-account-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

